import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Check } from './icons/HeroshhiIcons';

// ============================================
// EXCEL-STYLE MULTI-SELECT FILTER
// inline={true}: the list opens inside the page (used in the Filters panel,
// where a floating pop-up could be cut off at the panel's edge).
// ============================================
export default function ExcelFilterDropdown({
  options = [],
  selectedValues = [],
  onChange,
  placeholder = "Filter...",
  label = "Items",
  inline = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Close the floating version when clicking outside
  useEffect(() => {
    if (inline) return;
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [inline]);

  const filteredOptions = options.filter(opt => (opt || '').toLowerCase().includes(searchTerm.toLowerCase()));
  const isAllSelected = filteredOptions.length > 0 && filteredOptions.every(opt => selectedValues.includes(opt));

  const handleSelectAll = () => {
    if (isAllSelected) onChange(selectedValues.filter(v => !filteredOptions.includes(v)));
    else onChange([...new Set([...selectedValues, ...filteredOptions])]);
  };
  const toggleOption = (option) => {
    if (selectedValues.includes(option)) onChange(selectedValues.filter(v => v !== option));
    else onChange([...selectedValues, option]);
  };
  const activeCount = selectedValues.length;
  const box = (on) => (
    <span className={`w-4 h-4 flex-none rounded border flex items-center justify-center mr-3 ${on ? 'bg-[#10203A] border-[#10203A]' : 'border-gray-400'}`}>
      {on && <Check size={12} className="text-white" />}
    </span>
  );

  return (
    <div className="relative inline-block text-left w-full" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        className={`flex items-center justify-between w-full min-h-[42px] px-3 text-sm border rounded-lg transition-colors ${
          activeCount > 0 ? 'border-[#10203A] bg-slate-50 text-[#10203A] font-semibold' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
        }`}
      >
        <span className="truncate pr-2">{activeCount > 0 ? `${activeCount} ${label} selected` : placeholder}</span>
        <ChevronDown size={16} className={`text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className={inline
          ? 'mt-1 w-full bg-white border border-gray-200 rounded-lg'
          : 'absolute z-50 w-72 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl right-0 md:left-0 origin-top-left'}>
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                aria-label={`Search ${label}`}
                className="w-full pl-9 pr-3 min-h-[38px] bg-gray-50 border border-gray-200 rounded text-sm focus:bg-white"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className={`${inline ? 'max-h-56' : 'max-h-60'} overflow-y-auto p-2`}>
            {filteredOptions.length > 0 ? (
              <>
                <button type="button" onClick={handleSelectAll}
                  className="w-full flex items-center px-2 min-h-[36px] hover:bg-gray-50 rounded mb-1 border-b border-gray-100 text-left">
                  {box(isAllSelected)}<span className="text-sm font-semibold text-gray-700">(Select all)</span>
                </button>
                {filteredOptions.map((option, idx) => {
                  const isSelected = selectedValues.includes(option);
                  return (
                    <button type="button" key={idx} onClick={() => toggleOption(option)}
                      className="w-full flex items-center px-2 min-h-[36px] hover:bg-gray-50 rounded text-left">
                      {box(isSelected)}
                      <span className={`text-sm truncate ${isSelected ? 'text-gray-900 font-semibold' : 'text-gray-700'}`}>{option || 'Empty/Unassigned'}</span>
                    </button>
                  );
                })}
              </>
            ) : (
              <div className="py-4 text-center text-sm text-gray-500">No matching results found</div>
            )}
          </div>
          <div className="p-2 border-t border-gray-100 bg-gray-50 flex justify-end rounded-b-lg">
            <button type="button" onClick={() => setIsOpen(false)}
              className="min-h-[36px] px-4 text-sm font-bold text-white bg-[#10203A] rounded hover:bg-[#0b1729]">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}